import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { UserStore } from 'app/core/services/user.store';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { catchError, of, tap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HistoryService } from './history.service';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { UtilityService } from '../../services/utility.service';
import type { Entry, HistoryEntry, Module } from '../../../features/admin/modules/modules.model';
import { ConfirmDialogComponent } from '../../../features/admin/modules/modules-entity/templates/planning/views/map/ui/confirm-dialog';
import { FormHelper } from '../../../features/admin/modules/features/form-builder/form.util';
import {
  canReadGeneratedField,
  canReadFieldRight,
  effectiveFieldRightForKeyPath,
  effectiveFieldRightForResolvedField,
} from '../../../features/admin/modules/field-rights.util';

@Component({
  selector: 'app-history-viewer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule],
  template: `
    <h2 mat-dialog-title class="text-xl leading-7 text-black dark:text-white">Versiegeschiedenis: {{ title() }}</h2>
    <mat-dialog-content class="max-h-[70vh] overflow-y-auto" data-cy="history-viewer">
      @if (isLoading()) {
        <div class="my-4 flex justify-center">
          <mat-spinner
            class="[--mdc-circular-progress-active-indicator-color:#000] dark:[--mdc-circular-progress-active-indicator-color:#fff]"
            diameter="40"></mat-spinner>
        </div>
      }

      @if (errorMessage()) {
        <div class="my-4 text-center text-[#545559] dark:text-[#c7c7cc]">
          {{ errorMessage() }}
        </div>
      }

      @if (!isLoading() && !errorMessage() && historyEntries().length === 0) {
        <div class="my-4 text-center text-black dark:text-white">Geen geschiedenis beschikbaar.</div>
      }

      @if (!isLoading() && historyEntries().length > 0) {
        <ol class="m-0 list-none p-0" aria-label="Versiegeschiedenis">
          @for (historyEntry of historyEntries(); track trackByFn(i, historyEntry); let i = $index) {
            <li
              [class]="i === 0 ? 'py-2.5' : 'border-t border-[#eeeff1] py-2.5 dark:border-[#2c2c2e]'"
              data-cy="history-version">
              <!-- Details render only on request (design spec: expandable, keyboard-operable,
                   aria-expanded via a native button; focus ring is the only accent). -->
              <button
                type="button"
                data-cy="history-version-header"
                class="grid w-full cursor-pointer grid-cols-1 items-start gap-1 border-0 bg-transparent p-0 text-left
                       focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary
                       sm:grid-cols-[7rem_minmax(0,1fr)] sm:gap-3"
                [attr.aria-expanded]="isExpanded(i)"
                (click)="toggleVersion(i)">
                <span
                  [class]="
                    i === 0
                      ? 'text-xs font-medium leading-4 text-black dark:text-white'
                      : 'text-xs leading-4 text-[#545559] dark:text-[#c7c7cc]'
                  ">
                  {{ i === 0 ? 'Huidige versie' : (historyEntry.changeTimestamp | date: 'dd-MM-yyyy') }}
                </span>
                <span class="min-w-0">
                  <span class="block truncate text-sm font-medium leading-5 text-black dark:text-white">{{
                    historyEntryName(historyEntry.fullEntry)
                  }}</span>
                  <span
                    class="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-xs leading-4 text-[#545559] dark:text-[#c7c7cc]">
                    @if (historyEntry.updatedBy) {
                      <span>{{ historyEntry.updatedBy }}</span>
                    }
                    @if (historyEntry.changeTimestamp) {
                      <span>{{ historyEntry.changeTimestamp | date: 'dd-MM-yyyy HH:mm:ss' }}</span>
                    }
                    <span>{{ changeCountLabel(historyEntry) }}</span>
                  </span>
                </span>
              </button>
              <div class="grid w-full grid-cols-1 items-start gap-1 sm:grid-cols-[7rem_minmax(0,1fr)] sm:gap-3">
                <span class="hidden sm:block"></span>
                <div class="min-w-0">
                  @if (isExpanded(i)) {
                    @if (historyEntry.changes.length > 0) {
                      <div class="mt-2 border-t border-[#eeeff1] pt-2 dark:border-[#2c2c2e]">
                        <div
                          class="hidden grid-cols-3 gap-2 pb-1 text-xs font-medium leading-4 text-[#545559] dark:text-[#c7c7cc] sm:grid">
                          <div>Veld</div>
                          <div>Oud</div>
                          <div>Nieuw</div>
                        </div>
                        @for (change of historyEntry.changes; track trackByChange($index, change)) {
                          <div
                            class="grid grid-cols-1 gap-2 border-t border-[#eeeff1] py-1.5 text-sm dark:border-[#2c2c2e] sm:grid-cols-3">
                            <div class="min-w-0">
                              <div class="text-black dark:text-white">
                                <span>{{ change.field ? change.field.props?.label : change.fieldPath }}</span>
                                @if (change.field !== undefined) {
                                  <span class="ml-1 break-words text-xs leading-4 text-[#545559] dark:text-[#c7c7cc]">{{
                                    change.fieldPath
                                  }}</span>
                                }
                              </div>
                            </div>
                            <div class="min-w-0 break-words text-black dark:text-white">
                              <div class="text-xs font-medium leading-4 text-[#545559] dark:text-[#c7c7cc] sm:hidden">
                                Oud
                              </div>
                              <div [innerHTML]="formatValue(change.from, change.field)"></div>
                            </div>
                            <div class="min-w-0 break-words text-black dark:text-white">
                              <div class="text-xs font-medium leading-4 text-[#545559] dark:text-[#c7c7cc] sm:hidden">
                                Nieuw
                              </div>
                              <div [innerHTML]="formatValue(change.to, change.field)"></div>
                            </div>
                          </div>
                        }
                      </div>
                    } @else {
                      <div
                        class="mt-2 border-t border-[#eeeff1] pt-2 text-sm italic text-[#545559] dark:border-[#2c2c2e] dark:text-[#c7c7cc]">
                        Wijziging zonder specifieke velden.
                      </div>
                    }
                  }
                </div>
              </div>
            </li>
          }
        </ol>
      }
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-flat-button mat-dialog-close class="dd-action-button" [disabled]="isRestoring()">Sluiten</button>
    </mat-dialog-actions>

    <button mat-icon-button mat-dialog-close class="absolute top-2 right-2" color="light" [disabled]="isRestoring()">
      <mat-icon class="!text-black text-base dark:!text-white sm:text-lg">close</mat-icon>
    </button>
  `,
})
export class HistoryComponent implements OnInit {
  readonly userStore = inject(UserStore);
  public dialogRef = inject(MatDialogRef<HistoryComponent>);
  public data = inject<{ moduleId: Module['id']; entryId?: Entry['id']; title?: string }>(MAT_DIALOG_DATA);
  private historyService = inject(HistoryService);
  private modulesStore = inject(ModulesStore);
  private utilityService = inject(UtilityService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);

  isLoading = signal(true);
  isRestoring = signal(false);
  errorMessage = signal<string | null>(null);
  historyEntries = signal<HistoryEntry[]>([]);
  /** Row indexes whose field details are expanded — details render on request only. */
  expandedVersions = signal<Set<number>>(new Set());
  entry = signal<Entry | null>(null);
  module = signal<Module | null>(null);

  title = computed(() => {
    if (this.data.entryId === undefined && this.data.title) {
      return this.data.title;
    }
    return this.historyEntryName(this.entry() ?? this.module());
  });

  ngOnInit(): void {
    this.loadEntry();
    this.loadModule();
    this.loadHistory();
  }

  isExpanded(index: number): boolean {
    return this.expandedVersions().has(index);
  }

  toggleVersion(index: number): void {
    this.expandedVersions.update(current => {
      const next = new Set(current);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }

  loadEntry(): void {
    if (this.data.entryId) {
      this.entry.set(this.modulesStore.entries()[this.data.moduleId]?.find(entry => this.data.entryId === entry.id));
    }
  }

  loadModule(): void {
    this.module.set(this.modulesStore.getModuleById(this.data.moduleId));
  }

  loadHistory(): void {
    this.historyService
      .getHistory(this.data.moduleId, this.data.entryId)
      .pipe(
        catchError(error => {
          this.errorMessage.set('Er is een fout opgetreden bij het ophalen van de geschiedenis.');
          console.error('Fout bij het ophalen van geschiedenis:', error);
          return of([] as HistoryEntry[]);
        })
      )
      .subscribe(data => {
        this.historyEntries.set(this.visibleHistory(data));
        this.isLoading.set(false);
      });
  }

  /**
   * Hidden/technical form fields (className "hidden" — e.g. system-managed
   * schedule fields written by server-side actions) are not meaningful audit
   * information. Their changes are stripped, and versions without any visible
   * change are dropped entirely so system writes don't pollute the ledger.
   */
  visibleHistory(rows: HistoryEntry[]): HistoryEntry[] {
    const generatedForm = this.modulesStore.generatedForms()[this.data.moduleId];
    const currentRoleIds = this.userStore.currentUserRoles();
    return rows
      .map(row => ({
        ...row,
        changes: (row.changes ?? [])
          .map(change => ({
            ...change,
            field: FormHelper.getFieldByKeyPath(change.fieldPath, generatedForm) ?? change.field,
          }))
          .filter(change => {
            const field = FormHelper.getFieldByKeyPath(change.fieldPath, generatedForm) ?? change.field;
            return (
              !String(field?.className ?? '').includes('hidden') &&
              canReadFieldRight(
                effectiveFieldRightForResolvedField(
                  generatedForm,
                  change.fieldPath,
                  field,
                  currentRoleIds,
                  this.modulesStore.generatedForms()
                )
              )
            );
          }),
      }))
      .filter(row => row.changes.length > 0);
  }

  formatValue(value: any, field?: import('@ngx-formly/core').FormlyFieldConfig): string {
    if (value === undefined || value === null) {
      return '<span class="italic text-[#545559] dark:text-[#c7c7cc]">leeg</span>';
    }

    if (Array.isArray(value)) {
      return value
        .map(item => this.formatValue(item, field))
        .filter(Boolean)
        .join('<br>');
    }

    if (typeof value === 'object' && value?.selectedInput && !this.#canReadReferenceLabel(value, field)) {
      return 'Afgeschermd';
    }

    value = this.utilityService.valueToInterfaceValue(
      value,
      undefined,
      this.modulesStore.generatedForms()[this.module().id],
      this.module().id
    );

    if (value?.selectedInput) {
      return value.selectedInput;
    }

    return String(value);
  }

  #canReadReferenceLabel(value: Entry, field?: import('@ngx-formly/core').FormlyFieldConfig): boolean {
    const moduleOption = field?.props?.optionsArray?.find(option => option.optionsType === 'module');
    const ownerModule = value.moduleId
      ? this.modulesStore.getModuleById(value.moduleId)
      : moduleOption?.moduleCode
        ? this.modulesStore.getModuleByCode(moduleOption.moduleCode)
        : undefined;
    if (!value.moduleId && !moduleOption?.moduleCode) return true;
    if (!ownerModule) return false;
    const titleKeyPath = moduleOption?.moduleLinkedField ?? ownerModule.titelKeyPath;
    return canReadGeneratedField(
      this.modulesStore.generatedForms()[ownerModule.id],
      titleKeyPath,
      this.userStore.currentUserRoles()
    );
  }

  historyEntryName(snapshot: Entry | Module | null | undefined): string {
    if (!snapshot) {
      return '(geen titel)';
    }

    const titelKeyPath = this.module()?.titelKeyPath;
    const canReadTitle = canReadFieldRight(
      effectiveFieldRightForKeyPath(
        this.modulesStore.generatedForms()[this.data.moduleId],
        titelKeyPath,
        this.userStore.currentUserRoles()
      )
    );

    if (canReadTitle) {
      const selectedInput = (snapshot as { selectedInput?: unknown }).selectedInput;
      if (this.isNonEmptyString(selectedInput)) {
        return selectedInput;
      }

      const titleValue = titelKeyPath ? UtilityService.keyPathToValue(snapshot as Entry, titelKeyPath) : undefined;
      if (titleValue !== null && typeof titleValue === 'object') {
        if (this.isNonEmptyString(titleValue.selectedInput)) {
          return titleValue.selectedInput;
        }
      } else if (this.isNonEmptyPrimitive(titleValue)) {
        return String(titleValue);
      }
    }

    if (this.data.entryId === undefined) {
      const moduleSnapshotTitle = (snapshot as { title?: unknown }).title;
      if (this.isNonEmptyPrimitive(moduleSnapshotTitle)) {
        return String(moduleSnapshotTitle);
      }
      if (this.isNonEmptyString(this.module()?.title)) {
        return this.module()!.title;
      }
    }

    return '(geen titel)';
  }

  changeCountLabel(historyEntry: HistoryEntry): string {
    const count = historyEntry.changes.length;
    return `${count} ${count === 1 ? 'wijziging' : 'wijzigingen'}`;
  }

  private isNonEmptyString(value: unknown): value is string {
    return typeof value === 'string' && value.trim().length > 0;
  }

  private isNonEmptyPrimitive(value: unknown): value is string | number | boolean {
    return (
      (typeof value === 'string' && value.trim().length > 0) || typeof value === 'number' || typeof value === 'boolean'
    );
  }

  trackByFn(index: number, entry: HistoryEntry): any {
    return entry.changeTimestamp || index;
  }

  trackByChange(index: number, change): any {
    return index;
  }

  restoreVersion(historyEntry: HistoryEntry): void {
    if (!historyEntry.fullEntry) {
      this.snackBar.open('Kan deze versie niet herstellen: volledige invoergegevens ontbreken', 'Sluiten', {
        duration: 5000,
      });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Versie herstellen',
        message: `Weet je zeker dat je terug wilt naar de versie van ${new Date(historyEntry.changeTimestamp).toLocaleString()}?`,
        confirmText: 'Herstellen',
        cancelText: 'Annuleren',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        tap(confirmed => {
          if (confirmed) {
            this.isRestoring.set(true);
            return this.historyService.restoreToVersion(this.data.moduleId, historyEntry, this.entry()?.id);
          }
          return of(null);
        }),
        catchError(error => {
          this.utilityService.showMessage('Er is een fout opgetreden bij het herstellen van de versie');
          console.error('Fout bij herstellen van versie:', error);
          return of(null);
        })
      )
      .subscribe(result => {
        if (result) {
          this.utilityService.showMessage('Versie succesvol hersteld');

          // Close the dialog
          this.dialogRef.close(true);
        }
      });
  }
}

/**
 * Opens the entry-history viewer with the app-wide dialog configuration.
 * Single shared opener — callers must not duplicate this config inline.
 */
export function openHistoryDialog(
  dialog: MatDialog,
  data: { moduleId: Module['id']; entryId?: Entry['id']; title?: string }
): void {
  dialog.open(HistoryComponent, {
    width: '600px',
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: '90vh',
    data,
  });
}
